import {AttrsSchema, Component} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';
import throttle from 'lodash/throttle';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Persistence} from 'common/report/persistence';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {getReportName} from 'common/report/util';
import {SectionItem} from 'common/components/items-menu/types';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {LocalStorageKey} from 'common/report/constants';

import {
  ParsedPersistedSidenavState,
  PersistedSidenavState,
  SidenavAttrs,
  SidenavSection,
  SidenavSectionItem,
  SidenavState,
} from './types';
import {DropPosition, ScrollPosition, TrackingEvent} from './constants';
import {findAllSidenavIndices, moveSidenavItem} from './util';
import {template} from './template';
import css from './index.inline.styl';

export interface SidenavJsxProps {
  attrs: SidenavAttrs;
  on: {
    selectItem?: (ev: CustomEvent<SidenavSectionItem>) => void;
    sidenavResize?: (ev: CustomEvent<{width: number}>) => void;
    headerAction?: (ev: CustomEvent<{value: string}>) => void;
    footerAction?: (ev: CustomEvent<{value: string}>) => void;
    dragDrop?: (ev: CustomEvent<SidenavSection[]>) => void;
    overflowMenuSelect?: (ev: CustomEvent<{item: SidenavSectionItem; menuItem: SectionItem}>) => void;
  };
  props?: {
    sections: SidenavSection[];
    confirmNavigateFunc?: () => boolean;
  };
}

const COMPONENT_NAME = `mp-sidenav`;
const DEFAULT_PERSISTED_STATE: ParsedPersistedSidenavState = {
  collapsedSectionSet: new Set<string>(),
  expandedItemSet: new Set<string>(),
};

export class Sidenav extends Component<SidenavState, SidenavAttrs, any, any, IronContextRegistry> {
  private sectionsEl: HTMLDivElement;
  private sectionsElResizeObserver: ResizeObserver;
  private userLocalStorageHelper: UserLocalStorageHelper<PersistedSidenavState>;

  public confirmNavigateFunc?: () => boolean;

  static get attrsSchema(): AttrsSchema<SidenavAttrs> {
    return {
      selected: {type: `string`, description: `value of selected item`},
      loading: {type: `boolean`},
      'fixed-size': {type: `boolean`},
      'min-adjacent-width': {
        type: `number`,
        description: `minimum width for the horizontally adjacent content on the page. used to block resizing and collapsing the sidenav when necessary.`,
      },
      header: {type: `json`, tsType: `SidenavHeader`},
      footer: {type: `json`, tsType: `SidenavFooter`},
      'user-id': {type: `number`},
      'storage-key': {type: `string`, default: `default`},
      'is-nested-dashboards-enabled': {type: `boolean`},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`localStorageManager` as const],
      defaultContexts: {
        localStorageManager: new DummyStorageManager(),
      },
      defaultState: {
        activeDragSectionIdx: null,
        hoveredItemValues: new Set([]),
        isOverflowMenuOpen: false,
        scrollPosition: ScrollPosition.Top,
        sections: [],
        selectedItem: null,
        selectedSectionIndex: null,
        scrolledIntoItem: null,
        isCollapsed: false,
        ...DEFAULT_PERSISTED_STATE,
      },
      helpers: {
        onSelectItem: (ev) => {
          this.dispatchEvent(new CustomEvent(`selectItem`, {detail: ev.detail}));
        },

        toggleItemChildren: (ev) => {
          const expandedItemSet = new Set([...this.state.expandedItemSet]);

          const isExpanded = expandedItemSet.has(ev.detail.itemId);
          if (isExpanded) {
            expandedItemSet.delete(ev.detail.itemId);
          } else {
            expandedItemSet.add(ev.detail.itemId);
          }

          this.track(isExpanded ? TrackingEvent.CollapsedItem : TrackingEvent.ExpandedItem);
          this.update({expandedItemSet});
          this.setPersistedState();
          // wait until UI is refreshed
          setTimeout(this.throttledRefreshScrollPosition, 100);
        },

        getAllSelectedIndices: () => {
          return findAllSidenavIndices(this.state.sections, (_, item) => {
            return item.value === this.attr(`selected`);
          });
        },

        handleSidenavItemInserted: () => {
          if (!this.attr(`selected`) || this.state.scrolledIntoItem === this.attr(`selected`)) {
            return;
          }
          const selectedItemElement = this.el.querySelector(`.sidenav-item[value='${this.attr(`selected`)}']`);
          if (selectedItemElement) {
            selectedItemElement.scrollIntoView({
              block: `center`,
              behavior: `auto`,
            });
            this.update({scrolledIntoItem: this.attr(`selected`)});
          }
        },

        toggleSection: (sectionLabel: string) => {
          const collapsedSectionSet = new Set([...this.state.collapsedSectionSet]);
          const isCollapsed = collapsedSectionSet.has(sectionLabel);
          if (isCollapsed) {
            collapsedSectionSet.delete(sectionLabel);
          } else {
            collapsedSectionSet.add(sectionLabel);
          }

          this.track(isCollapsed ? TrackingEvent.ExpandedSection : TrackingEvent.CollapsedSection);
          this.update({collapsedSectionSet});
          this.setPersistedState();
          // wait until UI is refreshed
          setTimeout(this.throttledRefreshScrollPosition, 100);
        },

        onSectionsInserted: (node) => {
          this.sectionsEl = node.elm;
          this.sectionsEl.addEventListener(`scroll`, this.throttledRefreshScrollPosition);
          this.sectionsElResizeObserver = new ResizeObserver(() => this.throttledRefreshScrollPosition());
          this.sectionsElResizeObserver.observe(this.sectionsEl);
          this.throttledRefreshScrollPosition();
        },

        onFooterActionClick: (value: string) => {
          this.dispatchEvent(new CustomEvent(`footerAction`, {detail: {value}}));
        },

        onHeaderMenuChange: (ev) => {
          if (ev.detail.isOpen) {
            this.dispatchEvent(new CustomEvent(`headerMenuChange`, {detail: ev.detail}));
          }
        },

        onHeaderActionClick: (value: string) => {
          this.dispatchEvent(new CustomEvent(`headerAction`, {detail: {value}}));
        },

        onItemMouseEntered: (item: SidenavSectionItem) => {
          const hoveredItemValues = this.state.hoveredItemValues;
          if (!hoveredItemValues.has(item.value)) {
            this.update({
              hoveredItemValues: new Set([...hoveredItemValues, item.value]),
            });
          }
        },

        isExpandedStyle: () => {
          return !this.state.isCollapsed;
        },

        onItemDrop: (ev) => {
          const {from, to, dropPosition} = ev.detail;
          if (from.itemIdx === to.itemIdx) {
            return;
          }

          let toItemIdx = to.itemIdx;
          if (dropPosition === DropPosition.Above && toItemIdx > from.itemIdx) {
            toItemIdx = Math.max(toItemIdx - 1, 0);
          } else if (dropPosition === DropPosition.Below && toItemIdx < from.itemIdx) {
            toItemIdx += 1;
          }
          const sections = moveSidenavItem(
            this.state.sections,
            from.sectionIdx,
            from.itemIdx,
            to.sectionIdx,
            toItemIdx,
          );

          this.dispatchEvent(
            new CustomEvent(`dragDrop`, {
              detail: {
                sections,
              },
            }),
          );

          this.track(TrackingEvent.Reorder, {
            'section name': this.state.sections[from.sectionIdx].label,
            'section index': from.sectionIdx,
          });
        },

        onItemDragStart: (ev) => {
          this.update({activeDragSectionIdx: ev.detail.sectionIdx});
        },

        onItemDragEnd: () => {
          this.update({activeDragSectionIdx: null});
        },

        onItemOverflowMenuChange: (ev) => {
          this.update({isOverflowMenuOpen: ev.detail.isOpen});
        },

        onItemOverflowMenuSelect: (ev) => {
          propagateEvent(this, ev);
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    // TODO(jakub): leave this running for a few weeks to declutter user's localStorage
    // as we migrate to the preferred context approach
    Persistence.purge([`sidenav`]);

    this.userLocalStorageHelper = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.Sidenav,
    });

    this.update(this.getPersistedState());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.sectionsElResizeObserver?.disconnect();
    this.sectionsEl?.removeEventListener(`scroll`, this.throttledRefreshScrollPosition);
  }

  getMaxAvailableWidth(): number {
    return document.documentElement.clientWidth - this.attr(`min-adjacent-width`);
  }

  getPersistedState(): ParsedPersistedSidenavState {
    const persistedState = this.userLocalStorageHelper.getItem({
      key: this.attr(`storage-key`),
      userId: this.attr(`user-id`),
    });
    if (!persistedState) {
      return DEFAULT_PERSISTED_STATE;
    }

    return {
      collapsedSectionSet: new Set(persistedState.collapsedSectionSet),
      expandedItemSet: new Set(persistedState.expandedItemSet),
    };
  }

  private setPersistedState() {
    const {collapsedSectionSet, expandedItemSet} = this.state;

    let persistedState = this.userLocalStorageHelper.getItem({
      key: this.attr(`storage-key`),
      userId: this.attr(`user-id`),
    });

    persistedState = {collapsedSectionSet: [...collapsedSectionSet], expandedItemSet: [...expandedItemSet]};

    this.userLocalStorageHelper.setItem({
      userId: this.attr(`user-id`),
      key: this.attr(`storage-key`),
      value: persistedState,
    });
  }

  private track(eventName: TrackingEvent, props = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: COMPONENT_NAME,
      props: {
        tab: getReportName(),
        ...props,
      },
    });
  }
  private throttledRefreshScrollPosition = throttle(() => {
    if (this.sectionsEl.scrollHeight === this.sectionsEl.offsetHeight) {
      this.update({scrollPosition: ScrollPosition.None});
    } else if (this.sectionsEl.scrollTop === 0) {
      this.update({scrollPosition: ScrollPosition.Top});
    } else if (this.sectionsEl.scrollTop === this.sectionsEl.scrollHeight - this.sectionsEl.offsetHeight) {
      this.update({scrollPosition: ScrollPosition.Bottom});
    } else {
      this.update({scrollPosition: ScrollPosition.Scrolling});
    }
  }, 100);

  private getItemIdsForItem = (item: SidenavSectionItem, parentItemId: string) => {
    const currentItemId = `${parentItemId}-${item.value}`;

    if (item.nestedItems && item.nestedItems.length > 0) {
      return [currentItemId].concat(
        item.nestedItems.flatMap((nestedItem) => this.getItemIdsForItem(nestedItem, currentItemId)),
      );
    }
    return [currentItemId];
  };

  public set sections(sections: SidenavSection[]) {
    if (sections.length > 0) {
      const validItemIds = new Set(
        sections.flatMap((section, idx) => {
          return section.items.flatMap((item) => this.getItemIdsForItem(item, `${idx}`));
        }),
      );

      const expandedItemSet = new Set([...this.state.expandedItemSet].filter((itemId) => validItemIds.has(itemId)));

      if (expandedItemSet.size < this.state.expandedItemSet.size) {
        this.update({expandedItemSet});
        this.setPersistedState();
      }
    }

    this.update({sections});
  }
}

export const SidenavEl = defineAndMakeJsxFactory<SidenavJsxProps>(COMPONENT_NAME, Sidenav);
export const SidenavReact = panel2react<SidenavJsxProps>(COMPONENT_NAME, Sidenav);
